import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { findClearanceBySlug, BRAND_LOGO, CLEARANCE_PATH, WARRANTY_TEXT } from '../data/clearance';
import { PRICES, STOCK, RATES, PAYMENT, BUSINESS, EXCHANGE, DELIVERY } from '../data/pricing';
import { FREE_ABOVE } from '../data/pincodes';
import { formatINR, whatsAppLink } from '../lib/quote';
import { addToCart, openCart } from '../lib/cart';
import DeliveryCheck from '../components/DeliveryCheck';
import { track, FUNNEL } from '../lib/track';
import { Icon, Accordion, OfferRow } from './ProductDetail';

// One page per old-stock unit (approved design, Oct 2026). Google Shopping
// needs a page of its own for each product with a Buy button on it, so each
// clearance unit gets this page, in the same layout as the Daikin product
// page, sold through the same cart, checkout and Razorpay payment.
//
// Price and stock come from PRICES and STOCK in data/pricing.js under the
// unit's id; the unit itself from data/clearance.js. scripts/prerender.js
// writes the same price and availability into the static page and its
// Product schema, and scripts/generate-feed.js into the Merchant feed.

const DOMAIN = 'https://www.citicool.in';

function ClearanceSchema({ unit, price, inStock }) {
  useEffect(() => {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: unit.fullTitle,
      image: [`${DOMAIN}${unit.image}`],
      description: `${unit.fullTitle}, ${unit.year} model. Sealed pack, never installed. ${WARRANTY_TEXT}. ${unit.starNote}`,
      sku: unit.code,
      mpn: unit.code,
      brand: { '@type': 'Brand', name: unit.brand },
      offers: {
        '@type': 'Offer',
        url: `${DOMAIN}/${unit.slug}`,
        priceCurrency: 'INR',
        price: String(price),
        availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@type': 'Organization', name: BUSINESS.name },
      },
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify(data);
    el.setAttribute('data-product-schema', unit.id);
    document.head.appendChild(el);
    return () => el.remove();
  }, [unit, price, inStock]);
  return null;
}

export default function ClearanceDetail() {
  const { pathname } = useLocation();
  const unit = findClearanceBySlug(pathname);
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (unit) track(FUNNEL.PRODUCT_VIEW, { model: unit.id, source: 'clearance' });
  }, [unit]);

  if (!unit) return <Navigate to={`/${CLEARANCE_PATH}`} replace />;

  const kit = unit.kit;
  const price = (PRICES[unit.id] || {})[kit];
  if (typeof price !== 'number') return <Navigate to={`/${CLEARANCE_PATH}`} replace />;
  const unitsLeft = (STOCK[unit.id] || {})[kit] || 0;
  const inStock = unitsLeft > 0;
  const kitIncluded = kit === 'withKit';

  const bestExchange = EXCHANGE?.enabled && Array.isArray(EXCHANGE.copper) && EXCHANGE.copper.length
    ? Math.max(...EXCHANGE.copper.map((c) => c.value))
    : 0;

  const tiles = [
    unit.maxTemp && { title: `Cools up to ${unit.maxTemp}°C outside`, text: 'Made for Delhi peak summer', hot: true, icon: 'heat' },
    unit.feature && { title: unit.feature, text: unit.featureNote, icon: 'filter' },
    unit.iseer && { title: `ISEER ${unit.iseer}`, text: `${unit.stars} star*${unit.inverter ? ' inverter' : ''}`, icon: 'bolt' },
    (unit.coil || unit.refrigerant) && {
      title: [unit.coil, unit.refrigerant].filter(Boolean).join(', '),
      text: unit.kw ? `${unit.kw} kW cooling` : '',
      icon: 'chill',
    },
  ].filter(Boolean).slice(0, 4);

  const typeLabel = unit.inverter === true ? 'Inverter split AC' : unit.inverter === false ? 'Fixed speed split AC' : 'Split AC';
  const specs = [
    ['Brand', unit.brand],
    ['Model', unit.code],
    ['Model year', String(unit.year)],
    ['Condition', 'New, sealed pack'],
    ['Type', typeLabel],
    ['Capacity', unit.kw ? `${unit.soldAs} Ton (${unit.kw} kW)` : `${unit.soldAs} Ton`],
    ['BEE star rating', `${unit.stars} star*`],
    unit.iseer && ['ISEER', String(unit.iseer)],
    unit.refrigerant && ['Refrigerant', unit.refrigerant],
    unit.coil && ['Coil', unit.coil],
    unit.maxTemp && ['Cools in outdoor heat', `Up to ${unit.maxTemp}°C`],
    ['Piping kit', kitIncluded ? 'Included in the box' : 'Not included · available on request'],
    ['Warranty', WARRANTY_TEXT],
  ].filter(Boolean);

  const pageTitle = `${unit.fullTitle} Price ${formatINR(price)} | Clearance | Citi Cool`;
  const pageDescription = `${unit.fullTitle}, ${unit.year} model, at ${formatINR(price)} including GST. Star rating as per ${unit.year} BEE norms. Sealed pack. ${WARRANTY_TEXT}. ${inStock ? 'In stock' : 'Sold out'}, Delhi NCR delivery.`;

  const handleAdd = (thenGo) => {
    if (!inStock) return;
    addToCart(unit.id, kit, 1);
    track(FUNNEL.ADD_TO_CART, { model: unit.id, kit, qty: 1, value: price, source: 'clearance' });
    if (thenGo) {
      track(FUNNEL.CHECKOUT_STARTED, { value: price, units: 1, source: 'buy_now' });
      navigate('/checkout');
      return;
    }
    openCart();
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2500);
  };

  const askHref = whatsAppLink(inStock
    ? `Hi, I have a question about the ${unit.fullTitle} on your clearance page at ${formatINR(price)}.`
    : `Hi, the ${unit.fullTitle} on your clearance page is sold out. Do you have anything similar?`);
  const kitHref = whatsAppLink(`Hi, I would like the price of a copper piping kit for the ${unit.fullTitle} on citicool.in.`);
  const onWhatsApp = (source) => () => track(FUNNEL.WHATSAPP_HANDOFF, { model: unit.id, source });

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      <ClearanceSchema unit={unit} price={price} inStock={inStock} />

      <main className="mx-auto w-full max-w-5xl overflow-x-hidden px-4 pt-24 pb-28 lg:pb-16 lg:pt-28">
        <nav className="flex flex-wrap items-center gap-x-1 text-xs text-gray-500">
          <Link to={`/${CLEARANCE_PATH}`} className="hover:text-blue-600">Clearance sale</Link>
          <span className="mx-1.5">›</span>
          <span className="text-gray-700">{unit.brand}</span>
          <span className="mx-1.5">›</span>
          <span className="text-gray-700">{unit.soldAs} Ton</span>
        </nav>

        <div className="mt-4 grid w-full min-w-0 max-w-full gap-6 md:grid-cols-[minmax(0,340px)_minmax(0,1fr)] md:gap-8">
          <div className="relative aspect-[4/3] w-full self-start overflow-hidden rounded-xl border border-gray-100 bg-white">
            <img src={unit.image} alt={unit.fullTitle} width="1000" height="750" fetchpriority="high"
              className={`h-full w-full object-contain p-2.5 ${inStock ? '' : 'opacity-40'}`} />
            {BRAND_LOGO[unit.brand] && (
              <span className="absolute left-3 top-3 flex rounded-lg border border-gray-100 bg-white px-2 py-1.5">
                <img src={BRAND_LOGO[unit.brand]} alt={unit.brand} className="block h-3 w-auto lg:h-3.5" />
              </span>
            )}
          </div>

          <div className="min-w-0">
            <p className="text-xs font-semibold text-blue-700">{unit.brand} · Clearance · {unit.year} model</p>
            <h1 className="mt-0.5 break-words text-[17px] font-semibold leading-snug text-gray-900 sm:text-xl">{unit.displayFullTitle}</h1>
            <p className="mt-1 text-[13px] leading-snug text-gray-600">Sealed pack, never installed. {WARRANTY_TEXT}.</p>
            {/* Footnote for the asterisk on "3 Star*" (data/clearance.js). */}
            <p className="mt-1.5 text-xs leading-snug text-gray-600">{unit.starNote}</p>


            {/* Price */}
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={`text-2xl font-bold leading-none text-gray-900 ${inStock ? '' : 'line-through'}`}>{formatINR(price)}</span>
              {inStock ? (
                <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-[11px] font-bold text-red-700">
                  {unitsLeft === 1 ? 'Last unit' : `${unitsLeft} left`}
                </span>
              ) : (
                <span className="rounded-full bg-red-600 px-2.5 py-0.5 text-[11px] font-bold text-white">Sold out</span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-600">
              Inclusive of GST · <span className="font-semibold text-gray-900">EMI available at checkout</span>
            </p>

            {/* Why buy from us */}
            <div className="mt-3 grid grid-cols-2 divide-x divide-blue-100 rounded-lg bg-blue-50">
              <div className="flex items-start gap-2 px-2.5 py-2">
                <Icon name="store" className="mt-0.5 h-4 w-4 text-blue-700" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-snug text-blue-900">Sales and service under one roof</p>
                  <p className="text-[11px] leading-snug text-blue-800">No running around</p>
                </div>
              </div>
              <div className="flex items-start gap-2 px-2.5 py-2">
                <Icon name="shield" className="mt-0.5 h-4 w-4 text-blue-700" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-snug text-blue-900">
                    {unit.brand === 'Daikin' ? 'Authorised Daikin dealer since 2003' : `Authorised ${unit.brand} dealer`}
                  </p>
                  <p className="text-[11px] leading-snug text-blue-800">Cooling Delhi since 1990</p>
                </div>
              </div>
            </div>

            {/* Delivery */}
            <div className="mt-4">
              {inStock ? (
                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-green-700">
                  <Icon name="truck" className="h-4 w-4" />
                  In stock in our godown · Delhi NCR delivery
                </p>
              ) : (
                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-red-700">
                  <Icon name="clock" className="h-4 w-4" />
                  Sold out · this old stock will not come back
                </p>
              )}
              {inStock && <DeliveryCheck price={price} qty={1} inStock={inStock} />}
              <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                Delhi, Gurgaon, Noida, Faridabad and Ghaziabad. Charge includes GST, nothing added later.
                Free delivery above {formatINR(FREE_ABOVE)}.
              </p>
            </div>

            {/* Why people pick this AC */}
            {tiles.length > 0 && (
              <section className="mt-5">
                <h2 className="text-[15px] font-semibold text-gray-900">Why people pick this AC</h2>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {tiles.map((t) => (
                    <div key={t.title} className="flex flex-col gap-1 rounded-lg bg-gray-50 p-2.5">
                      <Icon name={t.icon} className={`h-[18px] w-[18px] ${t.hot ? 'text-orange-700' : 'text-blue-700'}`} />
                      <p className={`text-[13px] font-semibold leading-snug ${t.hot ? 'text-orange-700' : 'text-gray-900'}`}>{t.title}</p>
                      {t.text && <p className="text-xs leading-snug text-gray-600">{t.text}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Offers */}
            {inStock && (
              <div className="mt-5 divide-y divide-gray-200 rounded-lg border border-gray-200">
                {bestExchange > 0 && (
                  <OfferRow icon="exchange"
                    title={`Up to ${formatINR(bestExchange)} off your old AC`}
                    text="Any brand · free uninstallation · applied at checkout" />
                )}
                {kitIncluded ? (
                  <OfferRow icon="check" iconClass="text-green-700"
                    title="Copper piping kit included"
                    text="This unit ships with the piping kit in the box." />
                ) : (
                  <OfferRow icon="pipe" href={kitHref} onClick={onWhatsApp('clearance_kit_request')}
                    title="Copper piping kit at a discounted price"
                    text="Not in this box. On request when we call to confirm." />
                )}
                <OfferRow icon="wrench"
                  title="Installation, quoted before we start"
                  text="Say yes when you order. We visit, give a fixed price, then fit it." />
              </div>
            )}

            {/* Buttons */}
            <div className="mt-5">
              {inStock ? (
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => handleAdd(false)}
                    className="h-12 rounded-lg border-2 border-blue-700 bg-white text-[15px] font-semibold text-blue-700 transition hover:bg-blue-50">
                    {added ? 'Added to cart' : 'Add to cart'}
                  </button>
                  <button type="button" onClick={() => handleAdd(true)}
                    className="h-12 rounded-lg bg-blue-700 text-[15px] font-semibold text-white transition hover:bg-blue-800">
                    Buy now
                  </button>
                </div>
              ) : (
                <Link to={`/${CLEARANCE_PATH}`}
                  className="flex h-12 w-full items-center justify-center rounded-lg bg-blue-700 text-[15px] font-semibold text-white transition hover:bg-blue-800">
                  See the old stock still available
                </Link>
              )}
              <a href={askHref} target="_blank" rel="noopener noreferrer"
                onClick={onWhatsApp(inStock ? 'clearance_unit_page' : 'clearance_sold_out')}
                className="mt-1 flex h-10 items-center justify-center gap-1.5 text-sm font-semibold text-green-700 hover:text-green-800">
                <Icon name="chat" className="h-4 w-4" />
                {inStock ? 'Questions? Chat on WhatsApp' : 'Ask for similar stock on WhatsApp'}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 w-full min-w-0 max-w-3xl">
          <Accordion title="Specifications" defaultOpen>
            <dl className="w-full min-w-0 divide-y divide-gray-100">
              {specs.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-2">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="min-w-0 break-words text-right text-gray-900">{v}</dd>
                </div>
              ))}
            </dl>
          </Accordion>

          <Accordion title="Why old stock and not second hand">
            <p>
              This unit is from a previous season&apos;s stock and has never been opened or installed. You get
              brand new components, factory-clean coils and warranty as per OEM standards for the product type, which a second-hand AC
              cannot give you.
            </p>
          </Accordion>

          <Accordion title="Installation rates">
            <p className="mb-2 text-gray-500">
              Our published rates, before GST. If you want us to install, we visit, measure and give you
              a fixed quote at these rates before any work starts.
            </p>
            <dl className="w-full min-w-0 divide-y divide-gray-100">
              {[RATES.installSplit, RATES.nitrogenTest, RATES.copperSplit, RATES.drainPipe,
                RATES.controlWiring, RATES.standSplit1, RATES.standSplit15, RATES.dismantleOld,
                RATES.antiCorrosion, RATES.chiseling].filter(Boolean).map((r) => (
                <div key={r.label} className="flex justify-between gap-4 py-2">
                  <dt className="text-gray-500">{r.label}</dt>
                  <dd className="shrink-0 text-gray-900">
                    {formatINR(r.price)} <span className="text-gray-400">/ {r.per}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </Accordion>

          <Accordion title="Payment and delivery">
            <p>Equipment: {PAYMENT.equipment}.</p>
            <p className="mt-1">Installation: {PAYMENT.installation}.</p>
            <p className="mt-2.5 text-gray-500">{DELIVERY.footnote}</p>
            <p className="mt-2.5 text-gray-500">
              We install across {BUSINESS.serviceAreas.join(', ')}. GSTIN {BUSINESS.gstin}.
            </p>
          </Accordion>
        </div>

        <Link to={`/${CLEARANCE_PATH}`}
          className="mt-10 flex max-w-3xl items-center justify-between gap-3 rounded-xl border border-gray-200 p-4 transition hover:border-blue-400">
          <span>
            <span className="block text-[15px] font-semibold text-gray-900">More sealed-pack old stock</span>
            <span className="mt-0.5 block text-[13px] text-gray-600">O-General, Hitachi and Daikin at clearance prices</span>
          </span>
          <Icon name="chevron" className="h-5 w-5 text-blue-700" />
        </Link>


        {/* Sticky buy bar on phones, as on the Daikin product pages. */}
        {inStock && (
          <div className="fixed inset-x-0 bottom-0 z-50 flex items-center gap-2.5 border-t border-gray-200 bg-white pl-4 pr-3 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-2px_12px_rgba(0,0,0,0.08)] lg:hidden">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] text-gray-600">
                {unit.brand} · {unit.soldAs} Ton
                {unitsLeft === 1 && <> · <span className="font-semibold text-red-700">last unit</span></>}
              </p>
              <p className="text-base font-bold text-gray-900">{formatINR(price)}</p>
            </div>
            <button type="button" onClick={() => handleAdd(false)}
              className="h-11 shrink-0 rounded-xl border-2 border-blue-700 px-3 text-sm font-semibold text-blue-700">
              {added ? 'Added' : 'Add'}
            </button>
            <button type="button" onClick={() => handleAdd(true)}
              className="h-11 shrink-0 rounded-xl bg-blue-700 px-4 text-sm font-semibold text-white">
              Buy now
            </button>
          </div>
        )}
      </main>
    </>
  );
}
